import { z } from 'zod';
import type { Product, Money, Quantity } from '../shared/types';
import { ProductSchema, EntityBase, EntityType } from '../shared/types';

const VERSION_ONE = 1;

export class ProductEntity {

  private constructor(private readonly props: Product) { }

  static create(props: Omit<Product, keyof z.infer<typeof EntityBase>>): ProductEntity {
    const now = new Date();
    const product = ProductSchema.parse({
      ...props,
      _id: EntityType.enum.product + globalThis.crypto.randomUUID(),
      type: EntityType.enum.product,
      version: VERSION_ONE,
      createdAt: now,
      updatedAt: now,
    });
    return new ProductEntity(product);
  }

  static restore(props: Product): ProductEntity {
    const product = ProductSchema.parse(props);
    return new ProductEntity(product);
  }

  get _id(): string {
    return this.props._id;
  }

  get id(): string {
    return this.props._id;
  }

  get name(): string {
    return this.props.name;
  }

  get sku(): string {
    return this.props.sku;
  }

  get cost(): Money {
    return this.props.cost;
  }

  get price(): Money {
    return this.props.price;
  }

  get isActive(): boolean {
    return this.props.isActive;
  }

  get description(): string | undefined {
    return this.props.description;
  }

  get barcode(): string | undefined {
    return this.props.barcode;
  }

  get category(): string | undefined {
    return this.props.category;
  }

  get minQuantity(): Quantity | undefined {
    return this.props.minQuantity;
  }

  get maxQuantity(): Quantity | undefined {
    return this.props.maxQuantity;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get type(): string {
    return this.props.type;
  }

  get version(): number {
    return this.props.version;
  }

  get updatedAt(): Date {
    return this.props.updatedAt;
  }

  deactivate(): void {
    this.props.isActive = false;
    this.props.updatedAt = new Date();
  }

  updatePrice(price: Money): void {
    this.props.price = price;
    this.props.updatedAt = new Date();
  }

  updateCost(cost: Money): void {
    this.props.cost = cost;
    this.props.updatedAt = new Date();
  }

  toJSON(): Product {
    return { ...this.props };
  }
}
